import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import EditTeeTimeModal from './EditTeeTimeModal';
import { EVERYONE_GROUP, APP_WIDE_GROUP } from '../constants';

const fmtDate = (d) => {
  if (!d) return 'No date';
  const dateStr = String(d).includes('T') ? String(d).split('T')[0] : String(d);
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' });
};
const fmtTime = (t) => { const [h,m] = (t || '00:00').split(':').map(Number); return `${h%12||12}:${m.toString().padStart(2,'0')} ${h>=12?'PM':'AM'}`; };
const fmtTs = (ts) => new Date(ts).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' });

export default function TeeDetailModal({ tt, currentUser, onClose, onRefresh, readonly }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState((tt.groups || []).map(g => g.id));
  const [isEveryone, setIsEveryone] = useState(!!tt.is_everyone);
  const [isAppWide, setIsAppWide] = useState(!!tt.is_app_wide);
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const listRef = useRef(null);
  const isHost = tt.host_id === currentUser.id;

  const canPost = !readonly && (tt.players?.some(p => p.id === currentUser.id) || isHost);

  useEffect(() => {
    api.getComments(tt.id).then(setComments).catch(console.error);
    api.getGroups().then(setGroups).catch(() => {});
  }, [tt.id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const c = await api.postComment(tt.id, body.trim());
      setComments(prev => [...prev, c]);
      setBody('');
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setSending(false); }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const toggleGroupId = (id) => {
    setSelectedGroupIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const handleSaveGroups = async () => {
    setAssigningGroup(true);
    try {
      await api.updateTeeTime(tt.id, { groupIds: selectedGroupIds, isEveryone, isAppWide });
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setAssigningGroup(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <h3>{tt.course_name}</h3>
        <div style={{ background:'#f4f1eb', borderRadius:9, padding:'0.75rem', marginBottom:'0.9rem' }}>
          <div style={{ fontSize:13, color:'#4a4a3a' }}>{fmtDate(tt.tee_date)} at {fmtTime(tt.tee_time)} · Host: {tt.host_name}</div>
          {((tt.groups?.length > 0) || tt.is_everyone || tt.is_app_wide) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {tt.is_everyone && (
                <span style={{ fontSize:11, color:'#1a5c32', background:'#e8f5ec', borderRadius:8, padding:'1px 7px' }}>
                  {EVERYONE_GROUP.emoji} {EVERYONE_GROUP.name}
                </span>
              )}
              {tt.is_app_wide && (
                <span style={{ fontSize:11, color:'#7a5a0a', background:'#faf6e8', borderRadius:8, padding:'1px 7px' }}>
                  {APP_WIDE_GROUP.emoji} {APP_WIDE_GROUP.name}
                </span>
              )}
              {(tt.groups || []).map(g => (
                <span key={g.id} style={{ fontSize:11, color:'#534AB7', background:'#EEEDFE', borderRadius:8, padding:'1px 7px' }}>
                  {g.emoji} {g.name}
                </span>
              ))}
            </div>
          )}
          <div style={{ fontSize:12, color:'#2e7d4f', marginTop:2 }}>
            {tt.player_count}/{tt.total_spots} golfers confirmed
            {tt.invited_count > 0 && (
              <span style={{ color:'#9a8a5a' }}> · {tt.invited_count} invited</span>
            )}
          </div>
        </div>

        {isHost && !readonly && (
          <div style={{ marginBottom: '0.9rem', padding: '0.75rem', background: '#f4f1eb', borderRadius: 9 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#0d2b1a', marginBottom: 6 }}>Post to groups</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
              {/* Everyone — pinned at top */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, marginBottom: 2 }}>
                <input
                  type="checkbox"
                  checked={isEveryone}
                  onChange={() => setIsEveryone(v => !v)}
                  style={{ accentColor: EVERYONE_GROUP.color }}
                />
                {EVERYONE_GROUP.emoji} {EVERYONE_GROUP.name}
                <span style={{ fontSize: 10, color: '#7a7a6a', marginLeft: 4 }}>(your friends only)</span>
              </label>
              {/* App-Wide — discovery to any user nearby; no notifications */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, paddingBottom: 5, borderBottom: '1px solid #e8e4d8', marginBottom: 2 }}>
                <input
                  type="checkbox"
                  checked={isAppWide}
                  onChange={() => setIsAppWide(v => !v)}
                  style={{ accentColor: APP_WIDE_GROUP.color }}
                />
                {APP_WIDE_GROUP.emoji} {APP_WIDE_GROUP.name}
                <span style={{ fontSize: 10, color: '#7a7a6a', marginLeft: 4 }}>(anyone nearby)</span>
              </label>
              {groups.length > 0 ? groups.map(g => (
                <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(g.id)}
                    onChange={() => toggleGroupId(g.id)}
                    style={{ accentColor: g.color || '#2e7d4f' }}
                  />
                  {g.emoji} {g.name}
                </label>
              )) : (
                <div style={{ fontSize: 11, color: '#9a9a8a' }}>No custom groups yet.</div>
              )}
            </div>
            <button className="btn btn-accent btn-xs" onClick={handleSaveGroups} disabled={assigningGroup}>
              {assigningGroup ? 'Saving…' : 'Save groups'}
            </button>
          </div>
        )}

        <div className="thread">
          <div className="thread-header">
            <span>Group thread</span>
            <span>{comments.length} msg{comments.length !== 1 ? 's' : ''}</span>
          </div>

          <div ref={listRef} style={{ maxHeight:240, overflowY:'auto', display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'0.65rem' }}>
            {comments.length === 0
              ? <div style={{ fontSize:12, color:'#9a9a8a', textAlign:'center', padding:'0.5rem 0' }}>No messages yet.</div>
              : comments.map(c => (
                <div key={c.id} className="comment">
                  <div className="comment-av" style={{ background:'#2e7d4f' }}>{c.initials}</div>
                  <div className="comment-body">
                    <div className="comment-name">{c.author_name}</div>
                    <div className="comment-text">{c.body}</div>
                    <div className="comment-time">{fmtTs(c.created_at)}</div>
                  </div>
                </div>
              ))
            }
          </div>

          {canPost ? (
            <>
              <div className="comment-input">
                <textarea value={body} onChange={e => setBody(e.target.value)} onKeyDown={onKey}
                  placeholder="Message the group… (Enter to send)" rows={2} />
                <button className="btn btn-accent btn-sm" onClick={send} disabled={sending || !body.trim()}>
                  {sending ? '…' : 'Send'}
                </button>
              </div>
              <div className="notif-policy">✉️ Comments notify group via push + email only — no SMS.</div>
            </>
          ) : (
            <div style={{ fontSize:12, color:'#9a9a8a', textAlign:'center' }}>Join this tee time to post in the thread.</div>
          )}
        </div>

        <div className="modal-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <a
            href={api.getCalendarUrl(tt.id)}
            download
            className="btn btn-outline btn-sm"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            📅 Add to calendar
          </a>
          <div style={{ display: 'flex', gap: 8 }}>
            {isHost && !readonly && (
              <button className="btn btn-accent btn-sm" onClick={() => setShowEdit(true)}>Edit tee time</button>
            )}
            <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
      {showEdit && (
        <EditTeeTimeModal
          tt={tt}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onRefresh(); onClose(); }}
        />
      )}
    </div>
  );
}
